// SSEDashboard.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
} from "recharts";
import { API_URL } from "../../api-config";
function classNames(...arr) {
  return arr.filter(Boolean).join(" ");
}

const Card = ({ title, value, sub }) => (
  <div className="p-4 bg-[#1f2a40] rounded shadow">
    <div className="text-sm text-slate-300">{title}</div>
    <div className="text-2xl font-semibold text-slate-50">{value}</div>
    {sub && <div className="text-xs text-slate-400 mt-1">{sub}</div>}
  </div>
);

const number = (x, digits = 2) =>
  typeof x === "number" ? x.toFixed(digits) : x == null ? "-" : Number(x).toFixed(digits);

const groupBy = (arr, keyFn, aggFn) => {
  const map = new Map();
  arr.forEach((item) => {
    const k = keyFn(item);
    map.set(k, aggFn(map.get(k), item));
  });
  return Array.from(map.entries());
};

const SSEDashboard = ({  pollMs = 1000 }) => {
    const login = 2883007788;
  const [snapshot, setSnapshot] = useState(null);
  const [status, setStatus] = useState("connecting");
  const equityHistoryRef = useRef([]); // [{t, equity}]
  const [equitySeries, setEquitySeries] = useState([]);

  useEffect(() => {
    const url = login ? `${API_URL}/trading-api/reports/snapshot?login=${encodeURIComponent(login)}&poll_ms=${pollMs}` : `/sse/snapshot?poll_ms=${pollMs}`;
    const es = new EventSource(url);

    const onSnapshot = (evt) => {
      try {
        const data = JSON.parse(evt.data);
        setSnapshot(data);
        setStatus("open");
        console.log("SSE snapshot:", data);
        // equity timeline (cap last 120 puntos ~ 2 minutos con poll=1s)
        if (data?.account?.equity != null) {
          const now = new Date().toISOString().slice(11, 19); // HH:MM:SS
          equityHistoryRef.current = [
            ...equityHistoryRef.current,
            { t: now, equity: Number(data.account.equity) },
          ].slice(-120);
          setEquitySeries(equityHistoryRef.current);
        }
      } catch (e) {
        console.warn("SSE parse error:", e);
      }
    };

    es.addEventListener("snapshot", onSnapshot);
    es.onopen = () => setStatus("open");
    es.onerror = () => setStatus("retrying");

    return () => {
      es.removeEventListener("snapshot", onSnapshot);
      es.close();
    };
  }, [login, pollMs]);

  const positions = snapshot?.positions ?? [];
  const account = snapshot?.account ?? null;

  // ================== Derivados para gráficos ==================
  // Profit por posición (ticket)
  const pnlByTicket = useMemo(
    () =>
      positions.map((p) => ({
        ticket: String(p.ticket),
        profit: Number(p.profit || 0),
      })),
    [positions]
  );

  // Profit agregado por símbolo
  const pnlBySymbol = useMemo(() => {
    const grouped = groupBy(
      positions,
      (p) => p.symbol,
      (acc, p) => (acc || 0) + Number(p.profit || 0)
    );
    return grouped
      .map(([symbol, profit]) => ({ symbol, profit }))
      .sort((a, b) => Math.abs(b.profit) - Math.abs(a.profit));
  }, [positions]);

  // Cantidad de posiciones por símbolo
  const countBySymbol = useMemo(() => {
    const grouped = groupBy(
      positions,
      (p) => p.symbol,
      (acc) => (acc || 0) + 1
    );
    return grouped
      .map(([symbol, count]) => ({ symbol, count }))
      .sort((a, b) => b.count - a.count);
  }, [positions]);

  // Promedio de ganancia vs pérdida (basado en posición abierta)
  const avgProfitLoss = useMemo(() => {
    const wins = positions.filter((p) => Number(p.profit) > 0).map((p) => Number(p.profit));
    const losses = positions.filter((p) => Number(p.profit) < 0).map((p) => Number(p.profit));
    const avgWin = wins.length ? wins.reduce((a, b) => a + b, 0) / wins.length : 0;
    const avgLoss = losses.length ? losses.reduce((a, b) => a + b, 0) / losses.length : 0;
    return [
      { type: "Promedio Ganancia", valor: Math.round(avgWin) },
      { type: "Promedio Pérdida", valor: Math.round(avgLoss) },
    ];
  }, [positions]);

  // SL/TP definidos vs no definidos
  const sltpStats = useMemo(() => {
    let slOn = 0,
      slOff = 0,
      tpOn = 0,
      tpOff = 0;
    positions.forEach((p) => {
      const sl = p.sl == null ? null : Number(p.sl);
      const tp = p.tp == null ? null : Number(p.tp);
      if (sl && sl !== 0) slOn += 1;
      else slOff += 1;
      if (tp && tp !== 0) tpOn += 1;
      else tpOff += 1;
    });
    return [
      { tipo: "SL definido", valor: slOn },
      { tipo: "SL no definido", valor: slOff },
      { tipo: "TP definido", valor: tpOn },
      { tipo: "TP no definido", valor: tpOff },
    ];
  }, [positions]);

  // ================== UI ==================
  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold text-slate-100">Live Snapshot</h2>
          <p className="text-slate-400 text-sm">
            {snapshot
              ? `Login ${snapshot.login} • ${snapshot.server} • ${new Date(
                  snapshot.event_time
                ).toLocaleString()}`
              : "Conectando…"}
          </p>
        </div>
        <span
          className={classNames(
            "inline-flex items-center gap-2 rounded px-3 py-1 text-sm",
            status === "open"
              ? "bg-emerald-600/20 text-emerald-300"
              : "bg-yellow-600/20 text-yellow-300"
          )}
        >
          <span className="w-2 h-2 rounded-full bg-current"></span>
          {status === "open" ? "Conectado" : "Reconectando…"}
        </span>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card title="Balance" value={account ? `$ ${number(account.balance)}` : "-"} />
        <Card title="Equity" value={account ? `$ ${number(account.equity)}` : "-"} />
        <Card title="Free Margin" value={account ? `$ ${number(account.free_margin)}` : "-"} />
        <Card
          title="Margin Level"
          value={account ? `${number(account.margin_level)}%` : "-"}
          sub={account ? `Margin: $ ${number(account.margin)}` : ""}
        />
      </div>

      {/* Tabla simple de posiciones */}
      <div className="p-4 bg-[#1f2a40] rounded shadow overflow-auto">
        <h3 className="mb-3 font-semibold">Posiciones actuales</h3>
        <table className="min-w-full text-sm">
          <thead className="text-slate-300">
            <tr>
              <th className="text-left py-2 pr-4">Ticket</th>
              <th className="text-left py-2 pr-4">Símbolo</th>
              <th className="text-left py-2 pr-4">Tipo</th>
              <th className="text-right py-2 pr-4">Vol</th>
              <th className="text-right py-2 pr-4">Open</th>
              <th className="text-right py-2 pr-4">Precio</th>
              <th className="text-right py-2 pr-4">SL</th>
              <th className="text-right py-2 pr-4">TP</th>
              <th className="text-right py-2 pr-0">Profit</th>
            </tr>
          </thead>
          <tbody className="text-slate-200">
            {positions.map((p) => (
              <tr key={`${p.event_id}-${p.ticket}`} className="border-t border-slate-700/40">
                <td className="py-2 pr-4">{p.ticket}</td>
                <td className="py-2 pr-4">{p.symbol}</td>
                <td className="py-2 pr-4">{p.type_str?.replace("POSITION_TYPE_", "") || p.type}</td>
                <td className="py-2 pr-4 text-right">{number(p.volume, 3)}</td>
                <td className="py-2 pr-4 text-right">{number(p.price_open, 5)}</td>
                <td className="py-2 pr-4 text-right">{number(p.price_current, 5)}</td>
                <td className="py-2 pr-4 text-right">{p.sl ? number(p.sl, 5) : "-"}</td>
                <td className="py-2 pr-4 text-right">{p.tp ? number(p.tp, 5) : "-"}</td>
                <td
                  className={classNames(
                    "py-2 pr-0 text-right font-medium",
                    Number(p.profit) >= 0 ? "text-emerald-300" : "text-rose-300"
                  )}
                >
                  {number(p.profit, 2)}
                </td>
              </tr>
            ))}
            {positions.length === 0 && (
              <tr>
                <td className="py-6 text-center text-slate-400" colSpan={9}>
                  Sin posiciones abiertas
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Grillas de charts (barra, siguiendo tu referencia) */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Profit por posición */}
        <div className="p-4 bg-[#1f2a40] rounded shadow">
          <h3 className="mb-2 font-semibold">Profit por Posición (ticket)</h3>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={pnlByTicket}>
              <CartesianGrid stroke="#2a344b" />
              <XAxis dataKey="ticket" tick={{ fill: "#cbd5e1", fontSize: 11 }} />
              <YAxis tick={{ fill: "#cbd5e1" }} />
              <Tooltip contentStyle={{ backgroundColor: "#1f2a40", borderColor: "#2a344b" }} />
              <Bar dataKey="profit" fill="#34d399" />
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Profit por símbolo */}
        <div className="p-4 bg-[#1f2a40] rounded shadow">
          <h3 className="mb-2 font-semibold">Profit por Símbolo</h3>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={pnlBySymbol}>
              <CartesianGrid stroke="#2a344b" />
              <XAxis dataKey="symbol" tick={{ fill: "#cbd5e1", fontSize: 11 }} />
              <YAxis tick={{ fill: "#cbd5e1" }} />
              <Tooltip contentStyle={{ backgroundColor: "#1f2a40", borderColor: "#2a344b" }} />
              <Bar dataKey="profit" fill="#60a5fa" />
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Cantidad de posiciones por símbolo */}
        <div className="p-4 bg-[#1f2a40] rounded shadow">
          <h3 className="mb-2 font-semibold">Cantidad de Posiciones por Símbolo</h3>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={countBySymbol}>
              <CartesianGrid stroke="#2a344b" />
              <XAxis dataKey="symbol" tick={{ fill: "#cbd5e1", fontSize: 11 }} />
              <YAxis tick={{ fill: "#cbd5e1" }} />
              <Tooltip contentStyle={{ backgroundColor: "#1f2a40", borderColor: "#2a344b" }} />
              <Bar dataKey="count" fill="#fbbf24" />
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Promedio de ganancias vs pérdidas */}
        <div className="p-4 bg-[#1f2a40] rounded shadow">
          <h3 className="mb-2 font-semibold">Promedio: Ganancias vs Pérdidas</h3>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={avgProfitLoss}>
              <CartesianGrid stroke="#2a344b" />
              <XAxis dataKey="type" tick={{ fill: "#cbd5e1" }} />
              <YAxis tick={{ fill: "#cbd5e1" }} />
              <Tooltip contentStyle={{ backgroundColor: "#1f2a40", borderColor: "#2a344b" }} />
              <Bar dataKey="valor" fill="#facc15" />
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* SL/TP definidos vs no definidos */}
        <div className="p-4 bg-[#1f2a40] rounded shadow">
          <h3 className="mb-2 font-semibold">SL / TP definidos</h3>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={sltpStats}>
              <CartesianGrid stroke="#2a344b" />
              <XAxis dataKey="tipo" tick={{ fill: "#cbd5e1" }} />
              <YAxis tick={{ fill: "#cbd5e1" }} />
              <Tooltip contentStyle={{ backgroundColor: "#1f2a40", borderColor: "#2a344b" }} />
              <Bar dataKey="valor" fill="#f472b6" />
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Equity timeline (últimos snapshots) */}
        <div className="p-4 bg-[#1f2a40] rounded shadow">
          <h3 className="mb-2 font-semibold">Equity (últimos snapshots)</h3>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={equitySeries}>
              <CartesianGrid stroke="#2a344b" />
              <XAxis dataKey="t" tick={{ fill: "#cbd5e1", fontSize: 10 }} interval={equitySeries.length > 12 ? Math.floor(equitySeries.length / 12) : 0} />
              <YAxis tick={{ fill: "#cbd5e1" }} />
              <Tooltip contentStyle={{ backgroundColor: "#1f2a40", borderColor: "#2a344b" }} />
              <Bar dataKey="equity" fill="#8b5cf6" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
};

export default SSEDashboard;
